import React, { createContext, ReactNode, useContext, useState } from 'react'
import {
  ConversationState,
  InterruptOption,
  PromptTemplate as AgentPromptTemplate,
  StructuredJson,
} from 'src/queries/agent/network/agent.models'

export type AgentPlacement = 'top' | 'bottom' | 'left' | 'right'

interface AgentShortcutBase {
  refId: string
  placement: AgentPlacement
}

interface AgentShortcutWithPrompt extends AgentShortcutBase {
  initialPrompt: string
}

interface AgentShortcutWithPromptTemplate extends AgentShortcutBase {
  initialPromptTemplate: AgentPromptTemplate
}

export type AgentShortcut =
  | AgentShortcutWithPrompt
  | AgentShortcutWithPromptTemplate

export interface MessageState {
  sender: 'user' | 'ai'
  type: ConversationState
  timestamp: string
  content?: string
  contentSrc?: string
  options?: InterruptOption[]
  suggestedPrompts?: AgentPromptTemplate[]
  structuredJson?: StructuredJson
}

interface ChatHistory {
  messages: MessageState[]
  conversationState: ConversationState
  threadId?: string
}

interface AgentContextValue {
  // Agent open state
  isAgentOpen: boolean
  setIsAgentOpen: (isOpen: boolean) => void
  // Agent shortcut
  agentShortcut?: AgentShortcut
  setAgentShortcut: (shortcut: AgentShortcut | undefined) => void
  // Get messages for a specific chat key
  getMessages: (key: string) => MessageState[]
  // Add a message to a specific chat
  addMessage: (key: string, message: MessageState) => void
  // Update the last message in a specific chat
  updateLastMessage: (key: string, updates: Partial<MessageState>) => void
  // Clear all messages for a specific chat
  clearMessages: (key: string) => void
  // Get conversation state for a specific chat
  getConversationState: (key: string) => ConversationState
  // Set conversation state for a specific chat
  setConversationState: (key: string, state: ConversationState) => void
  // Get thread ID for a specific chat
  getThreadId: (key: string) => string | undefined
  // Set thread ID for a specific chat
  setThreadId: (key: string, threadId: string) => void
}

const AgentContext = createContext<AgentContextValue | undefined>(undefined)

interface AgentProviderProps {
  children: ReactNode
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
  const [chats, setChats] = useState<Record<string, ChatHistory>>({})
  const [isAgentOpen, setIsAgentOpen] = useState(false)
  const [agentShortcut, setAgentShortcutState] = useState<
    AgentShortcut | undefined
  >(undefined)

  const setAgentShortcut = (shortcut: AgentShortcut | undefined) => {
    setAgentShortcutState(shortcut)
  }

  const getMessages = (key: string): MessageState[] => {
    return chats[key]?.messages || []
  }

  const addMessage = (key: string, message: MessageState) => {
    setChats((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          messages: [],
          conversationState: ConversationState.MESSAGE,
        }),
        messages: [...(prev[key]?.messages || []), message],
      },
    }))
  }

  const updateLastMessage = (key: string, updates: Partial<MessageState>) => {
    setChats((prev) => {
      const chat = prev[key]
      if (!chat || chat.messages.length === 0) return prev

      const updatedMessages = [...chat.messages]
      const lastIndex = updatedMessages.length - 1
      updatedMessages[lastIndex] = {
        ...updatedMessages[lastIndex],
        ...updates,
      }

      return {
        ...prev,
        [key]: {
          ...chat,
          messages: updatedMessages,
        },
      }
    })
  }

  const clearMessages = (key: string) => {
    setChats((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          conversationState: ConversationState.MESSAGE,
        }),
        messages: [],
      },
    }))
  }

  const getConversationState = (key: string): ConversationState => {
    return chats[key]?.conversationState || ConversationState.MESSAGE
  }

  const setConversationState = (key: string, state: ConversationState) => {
    setChats((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { messages: [] }),
        conversationState: state,
      },
    }))
  }

  const getThreadId = (key: string): string | undefined => {
    return chats[key]?.threadId
  }

  const setThreadId = (key: string, threadId: string) => {
    setChats((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          messages: [],
          conversationState: ConversationState.MESSAGE,
        }),
        threadId,
      },
    }))
  }

  return (
    <AgentContext.Provider
      value={{
        isAgentOpen,
        setIsAgentOpen,
        agentShortcut,
        setAgentShortcut,
        getMessages,
        addMessage,
        updateLastMessage,
        clearMessages,
        getConversationState,
        setConversationState,
        getThreadId,
        setThreadId,
      }}
    >
      {children}
    </AgentContext.Provider>
  )
}

export const useAgentContext = (): AgentContextValue => {
  const context = useContext(AgentContext)
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentProvider')
  }
  return context
}
